// @upryzze/forms — safe-expression engine: parser (tokens → AST).
//
// Precedence (low→high): ternary, ||, &&, == !=, < <= > >=, + -, * / %,
// unary (! -), primary. Enforces node-count + nesting-depth caps so a
// crafted-but-syntactically-valid expression can't exhaust memory/stack.
//
// AST node types:
//   { type: 'num'|'str'|'bool', value }
//   { type: 'null' }
//   { type: 'field', name }
//   { type: 'call', name, args: Node[] }
//   { type: 'unary', op, arg }
//   { type: 'binary', op, left, right }       // arithmetic + comparison
//   { type: 'logical', op, left, right }       // && ||
//   { type: 'ternary', test, cons, alt }

import { tokenize, ExprSyntaxError } from "./tokenizer.js";
import { EXPR_LIMITS, EXPR_ERROR } from "./constants.js";

/** Parse an expression string into an AST. Throws ExprSyntaxError. */
export function parse(source) {
  const tokens = tokenize(source);
  const state = { tokens, i: 0, nodes: 0, depth: 0 };

  if (peek(state).type === "eof") {
    throw new ExprSyntaxError(EXPR_ERROR.EMPTY, "empty expression", 0);
  }

  const ast = parseTernary(state);
  const last = peek(state);
  if (last.type !== "eof") {
    throw new ExprSyntaxError(
      EXPR_ERROR.SYNTAX,
      `unexpected token ${JSON.stringify(last.value)}`,
      last.pos,
    );
  }
  return ast;
}

// ── token cursor helpers ──
const peek = (s) => s.tokens[s.i];
const next = (s) => s.tokens[s.i++];
function expectPunc(s, value) {
  const t = peek(s);
  if (t.type === "punc" && t.value === value) return next(s);
  throw new ExprSyntaxError(
    EXPR_ERROR.SYNTAX,
    `expected ${JSON.stringify(value)}`,
    t.pos,
  );
}
function isOp(t, ...vals) {
  return t.type === "op" && vals.includes(t.value);
}

// Count every constructed node; bail if the tree gets too big.
function node(s, obj) {
  s.nodes += 1;
  if (s.nodes > EXPR_LIMITS.MAX_NODES) {
    throw new ExprSyntaxError(
      EXPR_ERROR.TOO_LARGE,
      `expression exceeds ${EXPR_LIMITS.MAX_NODES} nodes`,
      peek(s).pos,
    );
  }
  return obj;
}

// Guard recursion depth around each descent.
function deeper(s, fn) {
  s.depth += 1;
  if (s.depth > EXPR_LIMITS.MAX_DEPTH) {
    throw new ExprSyntaxError(
      EXPR_ERROR.TOO_DEEP,
      `expression nesting exceeds ${EXPR_LIMITS.MAX_DEPTH}`,
      peek(s).pos,
    );
  }
  const result = fn();
  s.depth -= 1;
  return result;
}

// ── grammar ──
function parseTernary(s) {
  return deeper(s, () => {
    const test = parseLogicalOr(s);
    if (peek(s).type === "punc" && peek(s).value === "?") {
      next(s); // ?
      const cons = parseTernary(s);
      expectPunc(s, ":");
      const alt = parseTernary(s);
      return node(s, { type: "ternary", test, cons, alt });
    }
    return test;
  });
}

function parseLogicalOr(s) {
  let left = parseLogicalAnd(s);
  while (isOp(peek(s), "||")) {
    next(s);
    const right = parseLogicalAnd(s);
    left = node(s, { type: "logical", op: "||", left, right });
  }
  return left;
}

function parseLogicalAnd(s) {
  let left = parseEquality(s);
  while (isOp(peek(s), "&&")) {
    next(s);
    const right = parseEquality(s);
    left = node(s, { type: "logical", op: "&&", left, right });
  }
  return left;
}

function parseEquality(s) {
  let left = parseComparison(s);
  while (isOp(peek(s), "==", "!=")) {
    const op = next(s).value;
    const right = parseComparison(s);
    left = node(s, { type: "binary", op, left, right });
  }
  return left;
}

function parseComparison(s) {
  let left = parseAdditive(s);
  while (isOp(peek(s), "<", "<=", ">", ">=")) {
    const op = next(s).value;
    const right = parseAdditive(s);
    left = node(s, { type: "binary", op, left, right });
  }
  return left;
}

function parseAdditive(s) {
  let left = parseMultiplicative(s);
  while (isOp(peek(s), "+", "-")) {
    const op = next(s).value;
    const right = parseMultiplicative(s);
    left = node(s, { type: "binary", op, left, right });
  }
  return left;
}

function parseMultiplicative(s) {
  let left = parseUnary(s);
  while (isOp(peek(s), "*", "/", "%")) {
    const op = next(s).value;
    const right = parseUnary(s);
    left = node(s, { type: "binary", op, left, right });
  }
  return left;
}

function parseUnary(s) {
  if (isOp(peek(s), "!", "-")) {
    const op = next(s).value;
    return deeper(s, () => {
      const arg = parseUnary(s);
      return node(s, { type: "unary", op, arg });
    });
  }
  return parsePrimary(s);
}

function parsePrimary(s) {
  const t = peek(s);

  if (t.type === "num") {
    next(s);
    return node(s, { type: "num", value: t.value });
  }
  if (t.type === "str") {
    next(s);
    return node(s, { type: "str", value: t.value });
  }
  if (t.type === "bool") {
    next(s);
    return node(s, { type: "bool", value: t.value });
  }
  if (t.type === "null") {
    next(s);
    return node(s, { type: "null" });
  }

  // '(' expression ')'
  if (t.type === "punc" && t.value === "(") {
    next(s);
    return deeper(s, () => {
      const inner = parseTernary(s);
      expectPunc(s, ")");
      return inner;
    });
  }

  // identifier: either a function call `name(...)` or a bare field reference
  if (t.type === "ident") {
    next(s);
    if (peek(s).type === "punc" && peek(s).value === "(") {
      next(s); // (
      const args = [];
      if (!(peek(s).type === "punc" && peek(s).value === ")")) {
        // eslint-disable-next-line no-constant-condition
        while (true) {
          args.push(parseTernary(s));
          if (peek(s).type === "punc" && peek(s).value === ",") {
            next(s);
            continue;
          }
          break;
        }
      }
      expectPunc(s, ")");
      return node(s, { type: "call", name: t.value, args });
    }
    return node(s, { type: "field", name: t.value });
  }

  throw new ExprSyntaxError(
    EXPR_ERROR.SYNTAX,
    `unexpected token ${JSON.stringify(t.value)}`,
    t.pos,
  );
}
